import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, CheckCircle2, AlertTriangle, Ban } from "lucide-react";
import { fetchSuggestedVehicles } from "../../lib/fleetCalendar";
import { fmtCurrency } from "../../lib/status";

const AVAIL_META = {
  available: { icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50/70 text-emerald-900" },
  warning: { icon: AlertTriangle, cls: "border-amber-200 bg-amber-50/70 text-amber-900" },
  unavailable: { icon: Ban, cls: "border-slate-200 bg-slate-50 text-slate-600" },
  conflict: { icon: Ban, cls: "border-red-200 bg-red-50/70 text-red-900" },
};

/** Suggests genuinely-available vehicles for a window: reuses the exact
 * same ranking function that powers GET /bookings/{id}/replacement-candidates
 * (bookings/replacement_service.py), so a vehicle is never suggested here
 * that would be rejected elsewhere. */
export function SuggestedVehiclesPanel({ open, onClose, start, end, category, excludeItemId }) {
  const nav = useNavigate();
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !start || !end) return;
    setLoading(true);
    fetchSuggestedVehicles({
      start: start.toISOString(), end: end.toISOString(),
      category: category || undefined, exclude_item_id: excludeItemId || undefined,
    }).then((d) => setCandidates(d.candidates)).finally(() => setLoading(false));
  }, [open, start, end, category, excludeItemId]);

  if (!open) return null;

  const bookThis = (vehicleId) => {
    nav(`/app/bookings/new?vehicle_id=${vehicleId}&start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`);
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} data-testid="fc-suggest-backdrop" />
      <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-96 bg-white shadow-xl border-l border-slate-200 flex flex-col"
           data-testid="fc-suggest-panel">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold">Suggested vehicles</h3>
            {start && end && (
              <p className="text-xs text-slate-500 mt-0.5">
                {start.toLocaleDateString()} → {end.toLocaleDateString()}{category ? ` · ${category}` : ""}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="fc-suggest-close" className="p-1.5 rounded-md hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && candidates?.length === 0 && (
            <div className="text-sm text-slate-500" data-testid="fc-suggest-empty">
              No vehicles found for this window.
            </div>
          )}
          {!loading && candidates?.map((c) => {
            const meta = AVAIL_META[c.availability] || AVAIL_META.unavailable;
            const Icon = meta.icon;
            return (
              <div key={c.id} className={`rounded-xl border p-3 ${meta.cls}`} data-testid={`fc-suggest-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs opacity-75">{c.plate_number} · {c.category}</div>
                  </div>
                  <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                </div>
                <div className="text-sm font-mono mt-1">{fmtCurrency(c.price_per_day)}<span className="opacity-70 text-xs"> / day</span></div>
                {c.reasons?.length > 0 && (
                  <ul className="mt-1.5 text-[11px] opacity-80 space-y-0.5">
                    {c.reasons.map((r) => <li key={r}>✓ {r}</li>)}
                  </ul>
                )}
                {c.availability === "available" && (
                  <button onClick={() => bookThis(c.id)} data-testid={`fc-suggest-book-${c.id}`}
                          className="mt-2 w-full px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-xs font-medium hover:bg-[#2D6A4F]">
                    Book this vehicle
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
